import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { AttentionQueue } from './AttentionQueue';
import { ATTENTION_TABS, badgeOf, NO_OWNER_TO_REMIND, remindedWords, tabListHref, verbOf, whatOf, whenOf, whereOf } from './attention';
import { useRemind } from './hooks';
export function NeedsAttention({ data, filter, loading, today }) {
    const [tab, setTab] = useState('all');
    const counts = useMemo(()=>new Map((data?.counts ?? []).map((c)=>[
                c.kind,
                c.count
            ])), [
        data?.counts
    ]);
    const total = data?.total ?? 0;
    const tabs = ATTENTION_TABS.filter((t)=>t.value === 'all' || (counts.get(t.value) ?? 0) > 0);
    const current = tabs.some((t)=>t.value === tab) ? tab : 'all';
    const inTab = (data?.items ?? []).filter((it)=>current === 'all' || it.kind === current);
    const tabTotal = current === 'all' ? total : counts.get(current) ?? 0;
    const listHref = tabListHref(current, filter);
    const rows = inTab.map((it)=>{
        const when = whenOf(it, today);
        const verb = verbOf(it);
        const dated = it.kind === 'late' || it.kind === 'at_risk';
        return {
            id: `${it.kind}:${it.id}`,
            kind: it.kind,
            badge: badgeOf(it),
            what: whatOf(it),
            where: whereOf(it),
            when: {
                ...when,
                urgent: it.kind === 'late'
            },
            primary: it.rank === 0,
            extra: dated ? /*#__PURE__*/ _jsx(RemindAction, {
                ariaLabel: `Remind ${it.ownerName || 'the owner'}: ${whatOf(it)}`,
                label: "Remind",
                lastRemindedAt: it.lastRemindedAt,
                occurrenceId: it.occurrenceId,
                ownerName: it.ownerName,
                programId: it.programId,
                remindTestId: Testids.commandCenter.attentionRemind,
                remindedTestId: Testids.commandCenter.attentionReminded,
                variant: it.rank === 0 ? 'default' : 'secondary'
            }) : undefined,
            verb: {
                label: verb.label,
                href: verb.href,
                variant: dated ? 'ghost' : undefined
            }
        };
    });
    return /*#__PURE__*/ _jsx(AttentionQueue, {
        empty: total === 0 ? {
            title: 'Nothing needs attention',
            description: 'Nothing is late, at risk or waiting more than 2 days on approval, and no request or review item is open in these programs.'
        } : null,
        footer: listHref || tabTotal > inTab.length ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                listHref ? /*#__PURE__*/ _jsxs(Link, {
                    className: "text-[length:var(--fs-ui-body)] text-brand hover:underline",
                    "data-testid": Testids.commandCenter.attentionListLink,
                    to: listHref,
                    children: [
                        "Open ",
                        current === 'all' ? 'the late, at-risk and waiting dates' : 'them all',
                        " as a list →"
                    ]
                }) : null,
                tabTotal > inTab.length ? /*#__PURE__*/ _jsxs(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: [
                        (tabTotal - inTab.length).toLocaleString(),
                        " more not shown here",
                        listHref ? '; the list has them all' : ''
                    ]
                }) : null
            ]
        }) : null,
        loading: loading && !data,
        onTabChange: setTab,
        rows: rows,
        rule: "Ranked by consequence, then age",
        tab: current,
        tabs: tabs.map((t)=>({
                value: t.value,
                label: t.label,
                count: t.value === 'all' ? total : counts.get(t.value) ?? 0
            })),
        testids: {
            root: Testids.commandCenter.attention,
            tab: Testids.commandCenter.attentionTab,
            list: Testids.commandCenter.attentionList,
            row: Testids.commandCenter.attentionRow,
            verb: Testids.commandCenter.attentionVerb,
            more: Testids.commandCenter.attentionMore,
            empty: Testids.commandCenter.attentionEmpty
        },
        title: "Needs Attention"
    });
}
/**
 * Remind, or "Reminded 3h ago" while the date's last reminder is under a
 * day old. The server sends it to the date's owner as the viewer, and holds
 * a date to one reminder a day; a date no one owns cannot be reminded.
 */ export function RemindAction({ programId, occurrenceId, ownerName, lastRemindedAt, label, ariaLabel, remindTestId, remindedTestId, variant = 'secondary' }) {
    const remind = useRemind();
    const words = remindedWords(remind.data?.remindedAt || lastRemindedAt);
    if (words) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            as: "span",
            "data-testid": remindedTestId,
            size: "meta",
            tone: "muted",
            children: words
        });
    }
    return /*#__PURE__*/ _jsx(SpecraButton, {
        "aria-label": ariaLabel,
        "data-testid": remindTestId,
        disabled: !ownerName,
        isLoading: remind.isPending,
        onClick: ()=>remind.mutate({
                programId,
                occurrenceId
            }, {
                onSuccess: (r)=>toast.success(`Reminded ${r.recipientName || ownerName}.`),
                onError: (e)=>toast.error(apiErrorMessage(e, 'Could not send the reminder'))
            }),
        size: "sm",
        title: ownerName ? undefined : NO_OWNER_TO_REMIND,
        variant: variant,
        children: label
    });
}
